import { useState, useRef, useEffect, useCallback } from "react";
import APP_VERSION from "../version";
import API_ENDPOINTS from "../apiConfig";
import { getMajorMinor, getVersionsSince } from "../utils/versionUtils";
import WHATS_NEW_CONTENT from "../data/whatsNewContent";

/**
 * What's New popup for managers, and "last version used" for everyone:
 * users.last_seen_version is updated to the full app version (Major.Minor.Patch) for every user
 * (gate openers included, without a popup), so it doubles as "last app version".
 * `ready` must be true only once the site list (and with it the role) has loaded —
 * otherwise a manager would briefly look like a gate opener and miss the notes.
 */
export function useWhatsNew(isSiteManager, ready = true) {
  const [showModal, setShowModal] = useState(false);
  const [versions, setVersions] = useState([]);
  const hasChecked = useRef(false);
  const currentMajorMinor = getMajorMinor(APP_VERSION);

  useEffect(() => {
    if (!ready || hasChecked.current) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    hasChecked.current = true;

    fetch(API_ENDPOINTS.whatsNew, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (isSiteManager) {
          const newVersions = getVersionsSince(WHATS_NEW_CONTENT, data.last_seen_version, currentMajorMinor);
          if (newVersions.length > 0) {
            setVersions(newVersions);
            setShowModal(true);
          }
        }
        // record the full app version (e.g. 1.10.0) for everyone — "which version is this user on";
        // for managers it also acknowledges the notes, so a refresh doesn't re-show the modal.
        // What's New only compares Major.Minor, so the patch part never re-triggers the popup.
        if (data.last_seen_version !== APP_VERSION) {
          fetch(API_ENDPOINTS.whatsNew, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ version: APP_VERSION }),
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [ready, isSiteManager, currentMajorMinor]);

  const handleClose = useCallback(() => setShowModal(false), []);

  // Manual open from footer — show last 3 released versions, no acknowledgement
  const openModal = useCallback(() => {
    setVersions(getVersionsSince(WHATS_NEW_CONTENT, null, currentMajorMinor));
    setShowModal(true);
  }, [currentMajorMinor]);

  return { showModal, versions, handleClose, openModal };
}
